import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Text, Flex, SimpleGrid, Spinner, Input, Image } from "@chakra-ui/react";
import { MdLocationOn, MdAdd, MdChevronRight } from "react-icons/md";
import { Button } from "components/ui/Button";
import FormDialog from "components/utils/FormDialog";
import { useDialogForm } from "utils/useDialogForm";
import {
  useDashboardActividadesMeta,
  useDashboardActividades,
} from "features/dashboard/actividades/hooks";
import { useCreateDashboardLugar } from "features/dashboard/lugares/hooks";
import { sortLugaresForDisplay } from "features/dashboard/lugares/sort";

const Lugares = () => {
  const navigate = useNavigate();
  const metaQ = useDashboardActividadesMeta();
  const actsQ = useDashboardActividades();
  const createLugar = useCreateDashboardLugar();
  const addDialog = useDialogForm({ nombre: "" });

  const lugares = useMemo(
    () => sortLugaresForDisplay(metaQ.data?.lugares ?? []),
    [metaQ.data?.lugares]
  );
  const actividades = Array.isArray(actsQ.data) ? actsQ.data : [];

  const countsPorLugar = useMemo(() => {
    const counts = {};
    actividades.forEach((a) => {
      if (a.lugarActividadId) {
        counts[a.lugarActividadId] = (counts[a.lugarActividadId] ?? 0) + 1;
      }
    });
    return counts;
  }, [actividades]);

  const onAdd = async () => {
    const nombre = addDialog.form.nombre.trim();
    if (!nombre) return;
    try {
      await createLugar.mutateAsync(nombre);
      addDialog.close();
      addDialog.reset();
    } catch (_e) {}
  };

  return (
    <Box>
      {/* Header */}
      <Flex
        direction={{ base: "column", md: "row" }}
        align={{ base: "stretch", md: "center" }}
        justify="space-between"
        gap="4"
        mb="6"
      >
        <Box>
          <Text
            fontFamily="display"
            fontWeight="700"
            fontSize={{ base: "2xl", md: "3xl" }}
            color="dashboard.fg.default"
            lineHeight="1.1"
          >
            Lugares
          </Text>
          <Text fontSize="sm" color="dashboard.fg.muted" mt="0.5">
            Gestiona los lugares donde se realizan las actividades
          </Text>
        </Box>

        <Button
          color="red"
          variant="solid"
          onClick={() => addDialog.openWith()}
          radius="xl"
          fontSize="lg"
          alignSelf={{ base: "stretch", md: "auto" }}
        >
          <MdAdd />
          Nuevo lugar
        </Button>
      </Flex>

      {/* Loading */}
      {metaQ.isLoading && (
        <Flex justify="center" align="center" py="12">
          <Spinner />
        </Flex>
      )}

      {/* Error */}
      {metaQ.isError && (
        <pre style={{ color: "crimson", whiteSpace: "pre-wrap" }}>
          {String(metaQ.error?.message || metaQ.error)}
        </pre>
      )}

      {/* Empty state */}
      {!metaQ.isLoading && lugares.length === 0 && (
        <Box
          bg="dashboard.bg.surface"
          borderWidth="1px"
          borderColor="dashboard.border.muted"
          rounded="card"
          p="10"
          textAlign="center"
        >
          <Text color="dashboard.fg.muted">
            No hay lugares. Crea uno con <strong>Nuevo lugar</strong>.
          </Text>
        </Box>
      )}

      {/* Grid */}
      {!metaQ.isLoading && lugares.length > 0 && (
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} gap="4">
          {lugares.map((lugar) => {
            const count = countsPorLugar[lugar.id] ?? 0;
            return (
              <Box
                key={lugar.id}
                bg="dashboard.bg.surface"
                borderWidth="1px"
                borderColor="dashboard.border.muted"
                rounded="card"
                overflow="hidden"
                cursor="pointer"
                transition="transform 150ms ease, box-shadow 150ms ease"
                _hover={{ transform: "translateY(-2px)", boxShadow: "cardLift" }}
                onClick={() => navigate(`/dashboard/club/lugares/${lugar.id}`)}
              >
                {lugar.googlePhotoUri && (
                  <Image
                    src={lugar.googlePhotoUri}
                    alt={lugar.nombre}
                    w="100%"
                    h="118px"
                    objectFit="cover"
                    bg="dashboard.bg.subtle"
                  />
                )}
                <Flex align="center" gap="4" p="5">
                  <Box
                    w="48px"
                    h="48px"
                    borderRadius="14px"
                    bg={lugar.googlePhotoUri ? "whiteAlpha.900" : "blue.500"}
                    color={lugar.googlePhotoUri ? "blue.600" : "white"}
                    borderWidth={lugar.googlePhotoUri ? "1px" : "0"}
                    borderColor="dashboard.border.muted"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <MdLocationOn size="24px" />
                  </Box>
                  <Box flex={1} minW={0}>
                    <Text
                      fontFamily="display"
                      fontWeight="700"
                      fontSize="md"
                      color="dashboard.fg.default"
                      lineClamp={1}
                    >
                      {lugar.nombre}
                    </Text>
                    <Text fontSize="sm" color="dashboard.fg.muted" mt="0.5">
                      {count} {count === 1 ? "actividad" : "actividades"}
                    </Text>
                    {lugar.formattedAddress && (
                      <Text fontSize="xs" color="dashboard.fg.subtle" mt="1" lineClamp={1}>
                        {lugar.formattedAddress}
                      </Text>
                    )}
                  </Box>
                  <MdChevronRight
                    size="20px"
                    style={{ color: "var(--chakra-colors-dashboard-fg-subtle)", flexShrink: 0 }}
                  />
                </Flex>
              </Box>
            );
          })}
        </SimpleGrid>
      )}

      {/* Add dialog */}
      <FormDialog
        open={addDialog.open}
        onOpenChange={addDialog.onOpenChange}
        title="Nuevo lugar"
        saving={createLugar.isPending}
        onCancel={addDialog.close}
        onSave={onAdd}
        saveLabel="Crear"
        errorText={
          createLugar.isError
            ? String(createLugar.error?.message || createLugar.error)
            : ""
        }
      >
        <Input
          placeholder="Nombre del lugar"
          bg="dashboard.bg.input"
          borderColor="dashboard.border.default"
          {...addDialog.bind("nombre")}
          onKeyDown={(e) => e.key === "Enter" && onAdd()}
        />
      </FormDialog>
    </Box>
  );
};

export default Lugares;
